import { useState, useEffect, useMemo } from "react";
import useSortableData from "../../hooks/personnaliser/useSortableData";
import { ArrowLeft, ChevronUp, ChevronDown } from "lucide-react";
import MathText from '../VueQuestion/MathText';

/* ─── Palette (alignée AcquisitionSavoirsCards) ─── */
const T = {
  navy: "#1E3A8A", blueAccent: "#4A90D9",
  textPrimary: "#1A2744", textSecondary: "#6B7A99", textMuted: "#9AA5BD",
  borderLight: "rgba(30,58,138,0.06)", borderMid: "rgba(30,58,138,0.10)",
  green: "#22C55E", greenBg: "rgba(34,197,94,0.08)", greenBorder: "rgba(34,197,94,0.18)",
  orange: "#F59E0B", orangeBg: "rgba(245,158,11,0.08)", orangeBorder: "rgba(245,158,11,0.18)",
  red: "#EF4444", redBg: "rgba(239,68,68,0.07)", redBorder: "rgba(239,68,68,0.14)",
  gray: "#9CA3AF", grayBg: "rgba(156,163,175,0.07)", grayBorder: "rgba(156,163,175,0.15)",
};

/* ─── Status helpers ─── */
const getStatus = (m) => {
  if (m === null || m === undefined) return "neval";
  if (m >= 5) return "acquis";
  if (m > 3) return "encours";
  if (m >= 1) return "fragile";
  return "neval";
};

const CONF = {
  acquis:  { label: "Acquis",      color: T.green,  bg: T.greenBg,  border: T.greenBorder },
  encours: { label: "En cours",    color: T.orange, bg: T.orangeBg, border: T.orangeBorder },
  fragile: { label: "Non acquis",  color: T.red,    bg: T.redBg,    border: T.redBorder },
  neval:   { label: "Non évalué",  color: T.gray,   bg: T.grayBg,   border: T.grayBorder },
};

/* ─── Glassmorphism shared ─── */
const GLASS = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.88) 70%, rgba(235,243,255,0.82) 100%)",
  backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
  border: "1px solid rgba(255,255,255,0.7)",
  boxShadow: "0 12px 40px rgba(30,58,138,0.08), 0 4px 12px rgba(30,58,138,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
};

const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='150' height='150' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E\")";

/* ─── CSS responsive ─── */
const CSS = `
  .acq-root {
    --hdr-pad: 24px; --hdr-r: 18px;
    --stat-val: 26px; --stat-label: 10px;
    --title-fs: 16px; --meta-fs: 11px;
    --row-pad: 14px 18px; --row-gap: 6px; --row-r: 14px;
    --avatar-sz: 38px; --avatar-fs: 13px;
    --bar-w: 110px; --bar-h: 7px;
    --name-fs: 13px; --score-fs: 18px;
    --pill-fs: 10px; --pill-pad: 3px 10px;
    --sort-fs: 11px; --sort-pad: 5px 12px;
    --dist-h: 10px;
  }

  @media (max-width:639px) {
    .acq-root {
      --hdr-pad: 16px; --hdr-r: 14px;
      --stat-val: 20px; --stat-label: 9px;
      --title-fs: 14px; --meta-fs: 10px;
      --row-pad: 12px 14px; --row-gap: 5px; --row-r: 12px;
      --avatar-sz: 34px; --avatar-fs: 11px;
      --bar-w: 70px; --bar-h: 6px;
      --name-fs: 12px; --score-fs: 15px;
      --pill-fs: 9px; --pill-pad: 2px 8px;
      --sort-fs: 10px; --sort-pad: 4px 10px;
      --dist-h: 8px;
    }
  }

  @media (min-width:1280px) and (max-width:1699px) {
    .acq-root {
      --hdr-pad: 16px; --hdr-r: 14px;
      --stat-val: 20px; --stat-label: 9px;
      --title-fs: 13px; --meta-fs: 10px;
      --row-pad: 10px 14px; --row-gap: 4px; --row-r: 12px;
      --avatar-sz: 32px; --avatar-fs: 11px;
      --bar-w: 90px; --bar-h: 6px;
      --name-fs: 12px; --score-fs: 15px;
      --pill-fs: 9px; --pill-pad: 2px 8px;
      --sort-fs: 10px; --sort-pad: 4px 10px;
      --dist-h: 7px;
    }
  }

  @media (min-width:1700px) and (max-width:1919px) {
    .acq-root {
      --hdr-pad: 20px; --hdr-r: 16px;
      --stat-val: 22px; --stat-label: 9px;
      --title-fs: 14px; --meta-fs: 10px;
      --row-pad: 12px 16px; --row-gap: 5px; --row-r: 13px;
      --avatar-sz: 35px; --avatar-fs: 12px;
      --bar-w: 100px; --bar-h: 6px;
      --name-fs: 12px; --score-fs: 16px;
      --pill-fs: 10px; --pill-pad: 3px 9px;
      --sort-fs: 10px;
      --dist-h: 8px;
    }
  }

  /* 3xl 1920px+ → defaults */

  .acq-row {
    display: flex; align-items: center; gap: 14px;
    padding: var(--row-pad); border-radius: var(--row-r);
    border-left: 3px solid transparent;
    transition: all 0.25s cubic-bezier(.4,0,.2,1);
    position: relative; cursor: default;
  }
  .acq-row:hover {
    background: rgba(30,58,138,0.025);
    box-shadow: 0 2px 8px rgba(30,58,138,0.04);
  }

  .acq-avatar {
    width: var(--avatar-sz); height: var(--avatar-sz);
    border-radius: 12px; display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: var(--avatar-fs);
    color: white; flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  }

  .acq-bar-track {
    width: var(--bar-w); height: var(--bar-h);
    background: rgba(30,58,138,0.06); border-radius: 99px;
    overflow: hidden; flex-shrink: 0;
  }
  .acq-bar-fill {
    height: 100%; border-radius: 99px;
    transition: width 0.6s cubic-bezier(.4,0,.2,1);
  }

  .acq-sort-btn {
    padding: var(--sort-pad); border-radius: 8px;
    font-size: var(--sort-fs); font-weight: 600;
    cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; gap: 4px;
    border: 1.5px solid rgba(30,58,138,0.08);
    background: rgba(30,58,138,0.02); color: #6B7A99;
    font-family: inherit;
  }
  .acq-sort-btn:hover { background: rgba(30,58,138,0.05); color: #1A2744; }
  .acq-sort-active { background: rgba(74,144,217,0.07); color: #4A90D9; border-color: rgba(74,144,217,0.2); }

  .acq-filter-btn {
    padding: var(--pill-pad); border-radius: 20;
    font-size: var(--pill-fs); font-weight: 600;
    cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; gap: 5px;
    font-family: inherit;
  }
`;

/* ═══════════════════════════════════════════════════ */
const AcquisitionSavoirs = ({ selectedSavoir, setSelectedSavoir, eleves, viewLevel = 'savoir' }) => {
  const [savoirEtudiés, setSavoirEtudiés] = useState();
  const [statusFilter, setStatusFilter] = useState('all');

  const handleBack = () => setSelectedSavoir();

  // Filtre les résultats d'un élève selon le niveau de vue
  const matchResultat = (resultat, selected, level) => {
    if (level === 'descripteur') return resultat.descripteurId === selected.id;
    if (level === 'theme') return resultat.themeNom === selected.nom;
    const type = selected.isSavoirFaire ? 'savoir' : 'attendu';
    const id = selected.isSavoirFaire ? resultat.savoirFaireId : resultat.attenduId;
    return `${type}-${id}` === selected.uniqueSavoirKey;
  };

  useEffect(() => {
    if (!eleves || !selectedSavoir) return;
    if (viewLevel === 'savoir' && !selectedSavoir.savoirId) return;
    if (viewLevel === 'descripteur' && !selectedSavoir.id) return;
    if (viewLevel === 'theme' && !selectedSavoir.nom) return;

    const elevesMoyennes = {};

    eleves.eleves.forEach(eleve => {
      let totalScoreEleve = 0;
      let countEleve = 0;

      eleve.resultats?.forEach(resultat => {
        if (matchResultat(resultat, selectedSavoir, viewLevel) && resultat.scores) {
          totalScoreEleve += resultat.scores;
          countEleve += 1;
        }
      });

      elevesMoyennes[eleve.id] = {
        nom: eleve.nom,
        prenom: eleve.prenom,
        moyenne: countEleve > 0 ? Math.round((totalScoreEleve / countEleve) * 100) / 100 : null,
        nbEval: countEleve
      };
    });

    setSavoirEtudiés({
      elevesMoyennes: Object.values(elevesMoyennes)
    });
  }, [eleves, selectedSavoir, viewLevel]);

  // Distribution par statut
  const distribution = useMemo(() => {
    if (!savoirEtudiés) return { acquis: 0, encours: 0, fragile: 0, neval: 0, total: 0 };
    const d = { acquis: 0, encours: 0, fragile: 0, neval: 0 };
    savoirEtudiés.elevesMoyennes.forEach(s => { d[getStatus(s.moyenne)]++; });
    d.total = savoirEtudiés.elevesMoyennes.length;
    return d;
  }, [savoirEtudiés]);

  const pctAcquis = distribution.total > 0
    ? Math.round((distribution.acquis / distribution.total) * 100)
    : 0;

  // Filtrage par statut
  const filteredData = useMemo(() => {
    if (!savoirEtudiés) return [];
    if (statusFilter === 'all') return savoirEtudiés.elevesMoyennes;
    return savoirEtudiés.elevesMoyennes.filter(s => getStatus(s.moyenne) === statusFilter);
  }, [savoirEtudiés, statusFilter]);

  const titre = viewLevel === 'savoir' ? selectedSavoir?.savoirNom : selectedSavoir?.nom;
  const backLabel = viewLevel === 'theme' ? 'thèmes' : viewLevel === 'descripteur' ? 'notions' : 'savoirs';

  return (
    <div className="acq-root" style={{ padding: "8px 0" }}>
      <style>{CSS}</style>

      {/* ─── Bouton retour ─── */}
      <button
        onClick={handleBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 16px', borderRadius: 10, marginBottom: 16,
          fontSize: 'var(--meta-fs)', fontWeight: 600,
          color: 'white', background: '#F97316',
          border: 'none', cursor: 'pointer',
          transition: 'all 0.2s', fontFamily: 'inherit',
        }}
      >
        <ArrowLeft size={14} />
        Retour aux {backLabel}
      </button>

      {/* ─── Header glassmorphism ─── */}
      {selectedSavoir && (
        <div style={{
          ...GLASS, borderRadius: 'var(--hdr-r)', padding: 'var(--hdr-pad)',
          marginBottom: 14, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.015, pointerEvents: 'none', backgroundImage: GRAIN, mixBlendMode: 'multiply' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Badge thème */}
            {viewLevel !== 'theme' && selectedSavoir.themeNom && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10,
                padding: '4px 12px', borderRadius: 20,
                fontSize: 'var(--meta-fs)', fontWeight: 600,
                background: `${T.blueAccent}0D`, color: T.blueAccent,
                border: `1px solid ${T.blueAccent}22`,
              }}>
                📁 {selectedSavoir.themeNom}
              </div>
            )}

            {/* Titre */}
            <h2 style={{
              margin: 0, fontSize: 'var(--title-fs)', fontWeight: 700,
              color: T.textPrimary, lineHeight: 1.4, marginBottom: 16,
            }}>
              <MathText>{titre}</MathText>
            </h2>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { value: selectedSavoir.nbEleves, label: 'élèves', icon: '👥' },
                { value: selectedSavoir.moyenneClasse?.toFixed(1), suffix: '/6', label: 'moy. classe', icon: '📊' },
                { value: `${pctAcquis}%`, label: 'acquis', icon: '✅' },
              ].map((stat, i) => (
                <div key={i} style={{
                  padding: '10px 16px', borderRadius: 12,
                  background: 'rgba(30,58,138,0.03)', border: `1px solid ${T.borderLight}`,
                  textAlign: 'center', minWidth: 80,
                }}>
                  <div style={{
                    fontSize: 'var(--stat-val)', fontWeight: 800, fontFamily: 'monospace',
                    color: T.textPrimary, lineHeight: 1,
                  }}>
                    <span style={{ marginRight: 4 }}>{stat.icon}</span>
                    {stat.value ?? '—'}
                    {stat.suffix && <span style={{ fontSize: '0.5em', color: T.textMuted }}>{stat.suffix}</span>}
                  </div>
                  <div style={{
                    fontSize: 'var(--stat-label)', color: T.textMuted, fontWeight: 600,
                    marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── Distribution + filtres ─── */}
      {distribution.total > 0 && (
        <div style={{
          ...GLASS, borderRadius: 'var(--hdr-r)', padding: 'var(--hdr-pad)',
          marginBottom: 14, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.015, pointerEvents: 'none', backgroundImage: GRAIN, mixBlendMode: 'multiply' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: 'var(--meta-fs)', fontWeight: 700, color: T.textPrimary,
              marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              Distribution classe
            </div>

            {/* Barre horizontale */}
            <div style={{
              display: 'flex', gap: 3, height: 'var(--dist-h)', borderRadius: 99,
              overflow: 'hidden', background: 'rgba(30,58,138,0.05)', marginBottom: 14,
            }}>
              {distribution.acquis > 0 && <div style={{ flex: distribution.acquis, background: T.green, borderRadius: 99, transition: 'flex 0.4s' }} />}
              {distribution.encours > 0 && <div style={{ flex: distribution.encours, background: T.orange, borderRadius: 99, transition: 'flex 0.4s' }} />}
              {distribution.fragile > 0 && <div style={{ flex: distribution.fragile, background: T.red, borderRadius: 99, transition: 'flex 0.4s' }} />}
              {distribution.neval > 0 && <div style={{ flex: distribution.neval, background: T.gray, borderRadius: 99, transition: 'flex 0.4s' }} />}
            </div>

            {/* Filter pills */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: 'Tous', count: distribution.total, color: T.blueAccent, bg: `${T.blueAccent}0D`, border: `${T.blueAccent}22` },
                { key: 'acquis', label: 'Acquis', count: distribution.acquis, color: T.green, bg: T.greenBg, border: T.greenBorder },
                { key: 'encours', label: 'En cours', count: distribution.encours, color: T.orange, bg: T.orangeBg, border: T.orangeBorder },
                { key: 'fragile', label: 'Non acquis', count: distribution.fragile, color: T.red, bg: T.redBg, border: T.redBorder },
                { key: 'neval', label: 'Non évalué', count: distribution.neval, color: T.gray, bg: T.grayBg, border: T.grayBorder },
              ].map(f => {
                const active = statusFilter === f.key;
                return (
                  <button key={f.key} onClick={() => setStatusFilter(f.key)} style={{
                    padding: 'var(--pill-pad)', borderRadius: 20,
                    fontSize: 'var(--pill-fs)', fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 5,
                    fontFamily: 'inherit',
                    background: active ? f.bg : 'rgba(30,58,138,0.02)',
                    color: active ? f.color : T.textMuted,
                    border: active ? `1.5px solid ${f.border}` : `1.5px solid ${T.borderLight}`,
                  }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                    {f.label} ({f.count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ─── Liste des élèves ─── */}
      {savoirEtudiés && <StudentList data={filteredData} />}
    </div>
  );
};

export default AcquisitionSavoirs;

/* ═══════════════════════════════════════════════════ */
/* ─── Sous-composant : liste triable des élèves ─── */
/* ═══════════════════════════════════════════════════ */
const StudentList = ({ data }) => {
  const { items: sorted, requestSort, sortConfig } = useSortableData(data);

  const SortIcon = ({ field }) => {
    if (!sortConfig || sortConfig.key !== field) return null;
    return sortConfig.direction === 'ascending'
      ? <ChevronUp size={12} />
      : <ChevronDown size={12} />;
  };

  return (
    <div>
      {/* Barre de tri */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { key: 'nom', label: 'Nom' },
          { key: 'moyenne', label: 'Moyenne' },
          { key: 'nbEval', label: 'Évals' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => requestSort(s.key)}
            className={`acq-sort-btn ${sortConfig?.key === s.key ? 'acq-sort-active' : ''}`}
          >
            {s.label}
            <SortIcon field={s.key} />
          </button>
        ))}
      </div>

      {/* Lignes élèves */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--row-gap)',
        maxHeight: '60vh', overflowY: 'auto', paddingRight: 4,
      }}>
        {sorted && sorted.map((s, i) => {
          const st = getStatus(s.moyenne);
          const conf = CONF[st];
          const pct = s.moyenne != null ? Math.min((s.moyenne / 6) * 100, 100) : 0;

          return (
            <div
              key={i}
              className="acq-row"
              style={{
                borderLeft: `3px solid ${conf.color}`,
                background: 'rgba(255,255,255,0.6)',
                border: `1px solid ${T.borderLight}`,
                borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: conf.color,
              }}
            >
              {/* Avatar */}
              <div
                className="acq-avatar"
                style={{ background: `linear-gradient(135deg, ${conf.color}, ${conf.color}CC)` }}
              >
                {s.nom?.[0]}{s.prenom?.[0]}
              </div>

              {/* Nom + nb évals */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--name-fs)', fontWeight: 600, color: T.textPrimary }}>
                  {s.nom} <span style={{ fontWeight: 400, color: T.textSecondary }}>{s.prenom}</span>
                </div>
                <div style={{ fontSize: 'var(--meta-fs)', color: T.textMuted, marginTop: 2 }}>
                  {s.nbEval} compétence{s.nbEval > 1 ? 's' : ''} évaluée{s.nbEval > 1 ? 's' : ''}
                </div>
              </div>

              {/* Barre de progression (masquée < 640px) */}
              <div className="hidden sm:block">
                <div className="acq-bar-track">
                  <div className="acq-bar-fill" style={{ width: `${pct}%`, background: conf.color }} />
                </div>
              </div>

              {/* Score monospace */}
              <div style={{
                fontFamily: 'monospace', fontWeight: 700, fontSize: 'var(--score-fs)',
                color: conf.color, lineHeight: 1, minWidth: 50, textAlign: 'right',
              }}>
                {s.moyenne != null ? s.moyenne.toFixed(1) : '—'}
                <span style={{ fontSize: '0.6em', fontWeight: 500, color: T.textMuted }}>/6</span>
              </div>

              {/* Pill statut */}
              <span style={{
                padding: 'var(--pill-pad)', borderRadius: 20,
                fontSize: 'var(--pill-fs)', fontWeight: 700,
                background: conf.bg, color: conf.color,
                border: `1px solid ${conf.border}`,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {conf.label}
              </span>
            </div>
          );
        })}

        {sorted && sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: T.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>🔍</div>
            <div style={{ fontSize: 'var(--name-fs)', fontWeight: 500 }}>Aucun élève dans ce filtre</div>
          </div>
        )}
      </div>
    </div>
  );
};
